import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  EpicParityView, ParityArea, ParityAfterView, ParityBeforeView, ParityDivergence, ParityRuleStatus,
} from '../../../../shared/types/parity';
import { fetchEpicParity, approveParityUnderstanding, acceptParityDifference } from '../../lib/api/parity';
import { Button, StatusBadge, useConfirm, useToast, type StatusTone } from '../ui';

/**
 * FORGE-348 P1 — Parity Proof card on the epic's Quality tab.
 *
 * Two panels:
 *  - **Before** — "What your system does, and the tests that prove it": the
 *    Functional Understanding by area with each rule's status, then test
 *    strength (tests, coverage, mutation score) and the Before PDF.
 *  - **After** — "Parity: same tests, same results": per-area parity with
 *    confidence, the divergence diffs, and the After PDF.
 * Owner-only actions: approve the understanding (while Forge waits on it) and
 * accept a difference from a divergence row. Renders nothing for epics that
 * never ran Parity Proof.
 */

// FORGE-391: one rule label only. "Unconfirmed" = Forge was unsure (low
// confidence) and the client hasn't answered; it is pinned as the code behaves
// today. Confirmed / corrected / proposed labels confused clients — dropped.
const isUnconfirmed = (r: { status: ParityRuleStatus; confidence?: string; defectNote?: string | null }): boolean =>
  r.status === 'open' || (r.status === 'proposed' && r.confidence === 'low' && !r.defectNote);
const VERDICT_TONE: Record<string, StatusTone> = {
  READY: 'success', READY_WITH_GAPS: 'warning', PASS: 'success', PASS_WITH_EXCEPTIONS: 'warning', FAIL: 'danger',
};
const CONFIDENCE_TONE: Record<string, StatusTone> = { High: 'success', Medium: 'warning', Low: 'danger' };

const pct = (n: number | null | undefined): string => (typeof n === 'number' ? `${Math.round(n * 100)}%` : '—');
const label = (v: string | null | undefined): string => String(v ?? '').replace(/_/g, ' ');
const short = (h: string | null | undefined): string => (h ? `${h.slice(0, 12)}…` : '—');

const ReportLinks: React.FC<{ run: ParityBeforeView | ParityAfterView; testId: string }> = ({ run, testId }) => (
  <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
    {run.pdfUrl ? (
      <a href={run.pdfUrl} target="_blank" rel="noreferrer" className="font-medium text-primary-600 hover:underline" data-testid={`${testId}-pdf`}>
        Download PDF
      </a>
    ) : run.htmlUrl ? (
      <a href={run.htmlUrl} target="_blank" rel="noreferrer" className="font-medium text-primary-600 hover:underline" data-testid={`${testId}-html`}>
        View report
      </a>
    ) : (
      <span data-testid={`${testId}-pending`}>Report is being generated…</span>
    )}
    {run.reportId && <span>ID <code className="font-mono">{run.reportId}</code></span>}
    {run.reportHash && <span title={run.reportHash}>PDF SHA-256 <code className="font-mono">{short(run.reportHash)}</code></span>}
  </div>
);

const AreaUnderstanding: React.FC<{ area: ParityArea }> = ({ area }) => {
  const hasTests = area.rules.some((r) => typeof r.tests === 'number');
  return (
  <details className="rounded-md border border-slate-200 bg-white" data-testid="parity-area">
    <summary className="flex cursor-pointer items-center justify-between gap-2 px-3 py-2">
      <span className="min-w-0">
        <span className="text-sm font-medium text-slate-900">{area.name}</span>{' '}
        <span className="text-xs text-slate-500">{area.rules.length} rules</span>
      </span>
      {typeof area.tests === 'number' && (
        <span className="shrink-0 text-xs tabular-nums text-slate-600">
          {area.tests} tests · coverage {pct(area.coverage)} · mutation {pct(area.mutationScore)}
        </span>
      )}
    </summary>
    <div className="space-y-2 border-t border-slate-100 px-3 py-2">
      <p className="text-xs text-slate-600">{area.purpose}</p>
      {/* FORGE-392: a table — ID, plain-language rule, where it lives, how many tests pin it. */}
      <div className="overflow-x-auto">
        <table className="w-full table-fixed border-collapse text-xs" data-testid="parity-rules-table">
          <colgroup>
            <col className="w-24" /><col /><col className="w-56" />{hasTests && <col className="w-14" />}
          </colgroup>
          <thead>
            <tr className="border-b border-slate-200 text-left text-2xs uppercase tracking-wide text-slate-500">
              <th className="py-1.5 pr-2 font-semibold">Rule</th>
              <th className="py-1.5 pr-2 font-semibold">What the system does</th>
              <th className="py-1.5 pr-2 font-semibold">Code</th>
              {hasTests && <th className="py-1.5 text-right font-semibold">Tests</th>}
            </tr>
          </thead>
          <tbody>
            {area.rules.map((r) => (
              <tr key={r.id} className="border-b border-slate-100 align-top last:border-0" data-testid="parity-rule">
                <td className="py-1.5 pr-2">
                  <span className="font-mono font-semibold text-slate-800">{r.id}</span>
                  {isUnconfirmed(r) && (
                    <span className="mt-1 block"><StatusBadge className="whitespace-nowrap" tone="warning">⚠ Unconfirmed</StatusBadge></span>
                  )}
                </td>
                <td className="py-1.5 pr-2 text-slate-700">
                  {r.statement}
                  {r.defectNote && <span className="mt-0.5 block text-danger-700">Pre-existing defect: {r.defectNote}</span>}
                  {isUnconfirmed(r) && r.question && <span className="mt-0.5 block text-slate-500">Question: {r.question}</span>}
                </td>
                <td className="py-1.5 pr-2"><code className="block truncate font-mono text-[11px] text-slate-500" title={r.ref}>{r.ref}</code></td>
                {hasTests && (
                  <td className={`py-1.5 text-right tabular-nums ${r.tests ? 'text-slate-700' : 'font-semibold text-danger-700'}`}>{r.tests ?? 0}</td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {area.gaps && area.gaps.length > 0 && (
        <ul className="list-disc pl-4 text-xs text-warning-700">
          {area.gaps.map((g) => <li key={g}>{g}</li>)}
        </ul>
      )}
    </div>
  </details>
  );
};

const AcceptForm: React.FC<{ d: ParityDivergence; runId: string; repo: string; onDone: () => void }> = ({ d, runId, repo, onDone }) => {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [expires, setExpires] = useState('');
  const key = d.rules[0] ?? d.test;
  const m = useMutation({
    mutationFn: () => acceptParityDifference(runId, { repo, key, reason: reason.trim(), ...(expires ? { expiresAt: expires } : {}) }),
    onSuccess: () => { toast.success(`Accepted difference recorded for ${key}. It applies from the next /parity-after.`); setOpen(false); onDone(); },
    onError: (e: Error) => toast.error(e.message),
  });
  if (!open) {
    return <Button size="sm" variant="ghost" onClick={() => setOpen(true)} data-testid="parity-accept-open">Accept difference…</Button>;
  }
  return (
    <form
      className="mt-2 space-y-2 rounded-md bg-slate-50 p-2"
      onSubmit={(e) => { e.preventDefault(); m.mutate(); }}
      data-testid="parity-accept-form"
    >
      <label className="block text-xs text-slate-600" htmlFor={`reason-${d.test}`}>Why is this difference acceptable? (applies to <code>{key}</code>)</label>
      <textarea
        id={`reason-${d.test}`} value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
        className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
      />
      <div className="flex items-center gap-2">
        <label className="text-xs text-slate-600" htmlFor={`exp-${d.test}`}>Expires</label>
        <input id={`exp-${d.test}`} type="date" value={expires} onChange={(e) => setExpires(e.target.value)} className="rounded border border-slate-300 px-1 text-xs" />
        <Button size="sm" variant="primary" type="submit" loading={m.isPending} disabled={reason.trim().length < 10} data-testid="parity-accept-submit">Record</Button>
        <Button size="sm" variant="ghost" type="button" onClick={() => setOpen(false)}>Cancel</Button>
      </div>
    </form>
  );
};

const BeforePanel: React.FC<{ view: EpicParityView; onApproved: () => void }> = ({ view, onApproved }) => {
  const { confirm, dialog } = useConfirm();
  const toast = useToast();
  const before = view.before;
  const approve = useMutation({
    mutationFn: () => approveParityUnderstanding(before!.runId, view.repo),
    onSuccess: () => { toast.success('Understanding approved — Forge is writing the parity tests.'); onApproved(); },
    onError: (e: Error) => toast.error(e.message),
  });
  const onApprove = async () => {
    const ok = await confirm({
      title: 'Approve the Functional Understanding?',
      description: 'The parity tests will assert exactly these rules. Unanswered questions stay marked "⚠ Unconfirmed" in the report. Your code is not changed.',
      confirmLabel: 'Approve',
      tone: 'primary',
    });
    if (ok) approve.mutate();
  };
  return (
    <section className="space-y-3" data-testid="parity-before">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h4 className="text-sm font-semibold text-slate-900">Before — what your system does, and the tests that prove it</h4>
          <p className="text-xs text-slate-500">
            {view.understandingApprovedBy
              ? `Understanding approved by ${view.understandingApprovedBy}`
              : view.awaiting === 'understanding'
                ? `Waiting for your review (round ${view.reviewRound + 1} of 3)`
                : view.awaiting === 'scope' ? 'Waiting for scope answers on the GitHub issue' : `State: ${label(view.epicState)}`}
          </p>
        </div>
        {before?.verdict && <StatusBadge tone={VERDICT_TONE[before.verdict] ?? 'neutral'}>{label(before.verdict)}</StatusBadge>}
        {view.canApprove && (
          <Button size="sm" variant="primary" onClick={onApprove} loading={approve.isPending} data-testid="parity-approve">
            Approve understanding
          </Button>
        )}
      </div>
      {before?.totals && (
        <dl className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-4" data-testid="parity-before-totals">
          <div><dt className="text-slate-500">Tests</dt><dd className="font-semibold tabular-nums">{before.totals.tests}</dd></div>
          <div><dt className="text-slate-500">Passing on net48</dt><dd className="font-semibold tabular-nums">{pct(before.totals.passRate)}</dd></div>
          <div><dt className="text-slate-500">Coverage</dt><dd className="font-semibold tabular-nums">{pct(before.totals.coverage)}</dd></div>
          <div><dt className="text-slate-500">Mutation score</dt><dd className="font-semibold tabular-nums">{pct(before.totals.mutationScore)}</dd></div>
        </dl>
      )}
      <div className="space-y-2">
        {view.areas.map((a) => <AreaUnderstanding key={a.name} area={a} />)}
        {view.areas.length === 0 && <p className="text-xs text-slate-500">The Functional Understanding appears here once the analyst posts it.</p>}
      </div>
      {before && (before.pdfUrl || before.htmlUrl || before.verdict) && <ReportLinks run={before} testId="parity-before-report" />}
      {dialog}
    </section>
  );
};

const AfterPanel: React.FC<{ view: EpicParityView; after: ParityAfterView; onChanged: () => void }> = ({ view, after, onChanged }) => (
  <section className="space-y-3 border-t border-slate-100 pt-3" data-testid="parity-after">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div>
        <h4 className="text-sm font-semibold text-slate-900">After — same tests, same results</h4>
        {after.counts && (
          <p className="text-xs text-slate-500">
            {after.counts.tests} tests re-run · {after.counts.match} match · {after.counts.diverged} diverged · {after.counts.accepted} accepted · {after.counts.notComparable} not comparable
          </p>
        )}
      </div>
      {after.verdict && <StatusBadge tone={VERDICT_TONE[after.verdict] ?? 'neutral'}>{label(after.verdict)}</StatusBadge>}
    </div>
    {after.areas.length > 0 && (
      <table className="w-full text-xs" data-testid="parity-after-areas">
        <thead>
          <tr className="text-left text-slate-500"><th className="py-1">Area</th><th>Tests</th><th>Match</th><th>Diverged</th><th>Accepted</th><th>Confidence</th></tr>
        </thead>
        <tbody>
          {after.areas.map((a) => (
            <tr key={a.area} className="border-t border-slate-100">
              <td className="py-1 font-medium text-slate-800">{a.area}</td>
              <td className="tabular-nums">{a.tests}</td>
              <td className="tabular-nums">{a.match}</td>
              <td className="tabular-nums">{a.diverged}</td>
              <td className="tabular-nums">{a.accepted}</td>
              <td title={a.confidenceReason}><StatusBadge tone={CONFIDENCE_TONE[a.confidence] ?? 'neutral'}>{a.confidence}</StatusBadge></td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
    {after.divergences.map((d) => (
      <div key={d.test} className="rounded-md border border-danger-100 bg-danger-50/40 p-2" data-testid="parity-divergence">
        <div className="text-xs">
          <span className="font-semibold text-slate-800">{d.area ?? '—'}</span>
          {d.rules.length > 0 && <span className="font-mono text-slate-700"> · {d.rules.join(', ')}</span>}
          <span className="block truncate font-mono text-slate-500" title={d.test}>{d.test}</span>
          <span className="block text-slate-600">{d.reasons.join('; ')}</span>
        </div>
        {d.lines.length > 0 && (
          <pre className="mt-1 overflow-x-auto rounded bg-white p-1.5 font-mono text-[11px] leading-snug">
            {d.lines.map((l) => (
              <React.Fragment key={l.line}>
                <span className="text-danger-700">- {l.expected ?? '∅'}</span>{'\n'}
                <span className="text-success-700">+ {l.actual ?? '∅'}</span>{'\n'}
              </React.Fragment>
            ))}
          </pre>
        )}
        {view.canAccept && <AcceptForm d={d} runId={after.runId} repo={view.repo} onDone={onChanged} />}
      </div>
    ))}
    <ReportLinks run={after} testId="parity-after-report" />
  </section>
);

export const ParityCard: React.FC<{ epicId: string }> = ({ epicId }) => {
  const qc = useQueryClient();
  const query = useQuery({ queryKey: ['epic-parity', epicId], queryFn: () => fetchEpicParity(epicId), retry: false });
  const view = query.data;
  if (!view || !view.active) return null;
  const refresh = () => { void qc.invalidateQueries({ queryKey: ['epic-parity', epicId] }); };
  return (
    <div className="space-y-4 rounded-lg border border-gray-200 bg-white p-4" data-testid="parity-card">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900">Parity Proof</h3>
        <span className="text-xs text-gray-500">No functional change, shown before and after</span>
      </div>
      <BeforePanel view={view} onApproved={refresh} />
      {view.after && <AfterPanel view={view} after={view.after} onChanged={refresh} />}
    </div>
  );
};
